/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  buildEffectiveModelRepository,
  getModelRepositoryVersion,
  resetModelRepository,
  setCurrentModelRepository,
  subscribeModelRepository,
} from '../services/models/modelRepositoryRuntime';
import {
  extractActiveCatalogDefinitions,
  fetchActiveModelCatalogRows,
} from '../services/models/modelCatalogService';

const CONTEXT_KEY = '__GLOWGHOST_MODEL_REPOSITORY_CONTEXT__';
if (!globalThis[CONTEXT_KEY]) {
  globalThis[CONTEXT_KEY] = createContext(null);
}
const ModelRepositoryContext = globalThis[CONTEXT_KEY];

function applyCatalogRows(rows) {
  const definitions = extractActiveCatalogDefinitions(rows);
  if (definitions.length === 0) {
    resetModelRepository();
    return { mergedCount: 0 };
  }

  const repository = buildEffectiveModelRepository(definitions);
  setCurrentModelRepository(repository);
  return { mergedCount: definitions.length };
}

function runCatalogLoad(rows, handleFetchSuccess, handleFetchFailure) {
  try {
    return handleFetchSuccess(rows);
  } catch (error) {
    return handleFetchFailure(error);
  }
}

export const ModelRepositoryProvider = ({
  children,
  fetchCatalogRows = fetchActiveModelCatalogRows,
  autoLoad = true,
}) => {
  const [status, setStatus] = useState(autoLoad ? 'loading' : 'idle');
  const [error, setError] = useState(null);
  const [mergedCount, setMergedCount] = useState(0);
  const [version, setVersion] = useState(() => getModelRepositoryVersion());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeModelRepository(() => {
      if (!isMountedRef.current) return;
      setVersion(getModelRepositoryVersion());
    });
    return unsubscribe;
  }, []);

  const handleFetchSuccess = useCallback((rows) => {
    const result = applyCatalogRows(rows);
    if (isMountedRef.current) {
      setMergedCount(result.mergedCount);
      setError(null);
      setStatus('ready');
    }
    return result;
  }, []);

  const handleFetchFailure = useCallback((fetchError) => {
    console.error('[ModelRepositoryProvider] Failed to load catalog rows', fetchError);
    if (isMountedRef.current) {
      setError(fetchError instanceof Error ? fetchError : new Error(String(fetchError)));
      setStatus('error');
    }
    return { mergedCount: 0, error: fetchError };
  }, []);

  const refresh = useCallback(() => {
    if (typeof fetchCatalogRows !== 'function') {
      return Promise.resolve({ mergedCount: 0 });
    }
    return Promise.resolve()
      .then(() => fetchCatalogRows())
      .then((rows) => runCatalogLoad(rows, handleFetchSuccess, handleFetchFailure), handleFetchFailure);
  }, [fetchCatalogRows, handleFetchSuccess, handleFetchFailure]);

  useEffect(() => {
    if (!autoLoad) return undefined;
    if (typeof fetchCatalogRows !== 'function') return undefined;

    Promise.resolve()
      .then(() => fetchCatalogRows())
      .then((rows) => runCatalogLoad(rows, handleFetchSuccess, handleFetchFailure), handleFetchFailure);

    return undefined;
  }, [autoLoad, fetchCatalogRows, handleFetchSuccess, handleFetchFailure]);

  const value = useMemo(() => ({
    status,
    error,
    mergedCount,
    version,
    refresh,
  }), [status, error, mergedCount, version, refresh]);

  return (
    <ModelRepositoryContext.Provider value={value}>
      {children}
    </ModelRepositoryContext.Provider>
  );
};

export const useModelRepository = () => {
  const context = useContext(ModelRepositoryContext);
  if (!context) {
    throw new Error('useModelRepository must be used within a ModelRepositoryProvider');
  }
  return context;
};

export const useOptionalModelRepository = () => useContext(ModelRepositoryContext);

export default ModelRepositoryContext;
